import React from 'react';
import { Bell, BellOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { usePushNotifications } from '../hooks/usePushNotifications';

export function PushNotificationToggle({ token, onStatusChange }) {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe(token);
      if (success && onStatusChange) onStatusChange(false);
    } else {
      const success = await subscribe(token);
      if (success && onStatusChange) onStatusChange(true);
    }
  };

  const handleTest = async () => {
    await sendTestNotification(token);
  };

  if (!isSupported) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <AlertCircle className="h-4 w-4" />
        <span>Push notifications not supported in this browser</span>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-2 text-sm text-red-500">
        <BellOff className="h-4 w-4" />
        <span>Notifications blocked. Enable in browser settings.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Button
          onClick={handleToggle}
          disabled={loading}
          variant={isSubscribed ? "outline" : "default"}
          className={isSubscribed ? "border-green-500 text-green-600 hover:bg-green-50" : "bg-orange-500 hover:bg-orange-600"}
          data-testid="push-notification-toggle"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : isSubscribed ? (
            <Bell className="h-4 w-4 mr-2" />
          ) : (
            <BellOff className="h-4 w-4 mr-2" />
          )}
          {isSubscribed ? 'Notifications Enabled' : 'Enable Notifications'}
        </Button>
        
        {isSubscribed && (
          <Button
            onClick={handleTest}
            disabled={loading}
            variant="ghost"
            size="sm"
            className="text-gray-500"
            data-testid="test-notification-btn"
          >
            Test
          </Button>
        )}
      </div>
      
      {isSubscribed && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle className="h-4 w-4" />
          <span>You'll receive instant alerts for new jobs near you</span>
        </div>
      )}
      
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-500">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export default PushNotificationToggle;
